import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { ptBR as DatePtBr } from '@mui/x-date-pickers/locales';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import ArrowDownwardRoundedIcon from '@mui/icons-material/ArrowDownwardRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import SwapVertRoundedIcon from '@mui/icons-material/SwapVertRounded';
import moment from 'moment';
import {
  indeferirTitulosLote,
  listarEmpresasIndeferimentoLote,
  listarTitulosEmAbertoHojeBL
} from '../../../api/operacao';
import { getCodUsuarioLogado } from '../../../services/authentication/auth';
import AlertErroMain from '../../Shared/AlertErroMain';
import LoaderMain from '../../Shared/LoaderMain';
import { openSnackbarMain } from '../../Shared/openSnackbarMain';
import Main from '../../Main/Main';
import { formatarDocumento, limparDocumento } from '../../Utils/documentoAlfanumerico';
import './styles.scss';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const SELECTION_PAGE_SIZE = 200;
const TABLE_SKELETON_ROW_COUNT = 6;

const STATUS_OPTIONS = [
  { label: 'Em aberto', value: 'em_aberto' },
  { label: 'Indeferido', value: 'indeferido' },
  { label: 'Liquidado', value: 'liquidado' }
];
const TYPE_OPTIONS = [{ label: 'BL', value: 'BL' }];

const COLUMN_WIDTHS = {
  select: 43,
  situacao: 78,
  bordero: 92,
  carteira: 152.792,
  notaFiscal: 49.875,
  documento: 96.958,
  tipo: 56.667,
  vencimento: 97.854,
  valor: 102.719,
  cliente: 272,
  sacado: 304
};

const localizacaoBrasil = DatePtBr.components.MuiLocalizationProvider.defaultProps.localeText;
const padDatePart = value => String(value).padStart(2, '0');

const formatDateToBr = value => {
  if (!value) return '';

  if (value instanceof Date) {
    return `${padDatePart(value.getDate())}/${padDatePart(value.getMonth() + 1)}/${value.getFullYear()}`;
  }

  const valueAsString = String(value);

  if (valueAsString.includes('/')) {
    const [day, month, year] = valueAsString.split('/');

    if (day && month && year) {
      return `${padDatePart(day)}/${padDatePart(month)}/${year}`;
    }
  }

  const parsedDate = new Date(valueAsString);

  if (Number.isNaN(parsedDate.getTime())) {
    return valueAsString;
  }

  return `${padDatePart(parsedDate.getDate())}/${padDatePart(parsedDate.getMonth() + 1)}/${parsedDate.getFullYear()}`;
};

const getTodayBrDate = () => formatDateToBr(new Date());

const parseMomentDate = value => {
  if (!value) return null;

  const parsedDate = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);

  return parsedDate.isValid() ? parsedDate : null;
};

const createInitialFilters = () => ({
  situacao: 'em_aberto',
  tipoDocumento: 'BL',
  empresa: '',
  cpfCnpjCedente: '',
  vencimentoInicio: getTodayBrDate(),
  vencimentoFim: getTodayBrDate(),
  nomeSacado: '',
  cpfCnpjSacado: '',
  bordero: '',
  numeroDocumento: '',
  numeroNotaFiscal: ''
});

const formatDateMask = value => {
  const digits = String(value || '')
    .replace(/\D/g, '')
    .slice(0, 8);

  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;

  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const formatDocumentMask = value => formatarDocumento(limparDocumento(value).slice(0, 14));

const normalizeDocument = value => limparDocumento(value);

const normalizeTitleId = value => {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : value;
};

const sanitizeCommaSeparatedNumeric = value =>
  String(value || '')
    .replace(/[^\d,\s]/g, '')
    .replace(/\s+/g, '')
    .replace(/,+/g, ',')
    .replace(/^,|,$/g, '');

const countActiveFilters = filters =>
  Object.values(filters).reduce((count, value) => {
    if (typeof value === 'string') {
      return value.trim() ? count + 1 : count;
    }

    return value !== null && value !== undefined && value !== '' ? count + 1 : count;
  }, 0);

const formatCurrency = value =>
  new Intl.NumberFormat('pt-BR', {
    currency: 'BRL',
    style: 'currency'
  }).format(Number(value || 0));

const formatNumber = value => new Intl.NumberFormat('pt-BR').format(Math.max(Number(value || 0), 0));

const formatDisplayDate = value => {
  if (!value) return '-';

  if (String(value).includes('/')) {
    return formatDateMask(value);
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return String(value);
  }

  return new Intl.DateTimeFormat('pt-BR').format(parsedDate);
};

const normalizeStatusValue = value =>
  String(value || '')
    .trim()
    .toLowerCase();
const normalizeAnalysisStatusValue = value =>
  String(value || '')
    .trim()
    .toUpperCase();
const isLiquidadoStatus = value => ['s', 'liquidado'].includes(normalizeStatusValue(value));
const isOpenStatus = value => ['n', 'em aberto'].includes(normalizeStatusValue(value));
const isIndeferidoAnalysisStatus = value => normalizeAnalysisStatusValue(value) === 'I';

const resolveStatusLabel = (item, situacao) => {
  if (item) {
    const liquidacao = item.LIQUIDACAO;
    const statusAnalise = normalizeAnalysisStatusValue(item.STATUSANALISE);
    const hasLiquidacao = liquidacao !== null && liquidacao !== undefined && liquidacao !== '';

    if (hasLiquidacao && statusAnalise === 'D') {
      return 'Liquidado';
    } else if (!hasLiquidacao && statusAnalise === 'D') {
      return 'Em aberto';
    } else if (!hasLiquidacao && statusAnalise === 'I') {
      return 'Indeferido';
    }
  }

  const statusValue = item?.SITUACAO ?? item?.STATUS ?? situacao;

  if (isLiquidadoStatus(statusValue)) {
    return 'Liquidado';
  }

  if (isOpenStatus(statusValue) || !statusValue) {
    return 'Em aberto';
  }

  return String(statusValue);
};

const resolveStatusDisplayLabel = status => {
  const normalizedStatus = normalizeStatusValue(status).replace(/_/g, ' ');

  if (normalizedStatus === 'em aberto') {
    return 'Em aberto';
  }

  if (normalizedStatus === 'liquidado') {
    return 'Liquidado';
  }

  if (normalizedStatus === 'indeferido') {
    return 'Indeferido';
  }

  return status;
};

const isRowSelectable = row =>
  !isLiquidadoStatus(row?.situacao) && !isIndeferidoAnalysisStatus(row?.statusAnalise);

const parseDateForSort = value => {
  if (!value || value === '-') return null;

  const parsedDate = moment(value, ['DD/MM/YYYY', 'YYYY-MM-DD'], true);

  return parsedDate.isValid() ? parsedDate.valueOf() : null;
};

const compareSortValues = (leftValue, rightValue) => {
  const leftIsEmpty = leftValue === null || leftValue === undefined || leftValue === '';
  const rightIsEmpty = rightValue === null || rightValue === undefined || rightValue === '';

  if (leftIsEmpty && rightIsEmpty) return 0;
  if (leftIsEmpty) return 1;
  if (rightIsEmpty) return -1;

  if (typeof leftValue === 'number' && typeof rightValue === 'number') {
    return leftValue - rightValue;
  }

  return String(leftValue).localeCompare(String(rightValue), 'pt-BR', {
    numeric: true,
    sensitivity: 'base'
  });
};

const getRowSortValue = (row, field) => {
  switch (field) {
    case 'situacao':
      return row.situacao;
    case 'borderoNumero':
      return row.borderoNumero === '-' ? null : row.borderoNumero;
    case 'carteira':
      return row.carteira;
    case 'notaFiscal':
      return row.notaFiscal === '-' ? null : row.notaFiscal;
    case 'documento':
      return row.documento === '-' ? null : row.documento;
    case 'tipo':
      return row.tipo;
    case 'vencimento':
      return parseDateForSort(row.vencimento);
    case 'valor':
      return row.valor;
    case 'cliente':
      return row.cliente;
    case 'sacado':
      return row.sacado;
    default:
      return row[field];
  }
};

const sortRows = (rows, sortConfig) => {
  if (!sortConfig.field) {
    return rows;
  }

  const directionMultiplier = sortConfig.direction === 'desc' ? -1 : 1;

  return [...rows].sort((leftRow, rightRow) => {
    const comparison = compareSortValues(
      getRowSortValue(leftRow, sortConfig.field),
      getRowSortValue(rightRow, sortConfig.field)
    );

    if (comparison !== 0) {
      return comparison * directionMultiplier;
    }

    return compareSortValues(leftRow.id, rightRow.id);
  });
};

const getStatusStyles = status => {
  if (status === 'Liquidado') {
    return {
      backgroundColor: '#dcfce7',
      color: '#166534',
      dotColor: '#00c950',
      height: 16,
      minWidth: 60
    };
  }

  if (status === 'Indeferido') {
    return {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      dotColor: '#dc2626',
      height: 16,
      minWidth: 60
    };
  }

  if (status === 'Cancelado') {
    return {
      backgroundColor: '#fee2e2',
      color: '#991b1b',
      dotColor: '#fb2c36',
      height: 16,
      minWidth: 60
    };
  }

  return {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    dotColor: '#2b7fff',
    height: 16,
    minWidth: 60
  };
};

const buildListPayload = ({ codUsuario, filters, page, perPage }) => {
  const payload = {
    cod_usuario: codUsuario,
    page,
    per_page: perPage
  };

  if (filters.tipoDocumento) {
    payload.tipo_documento = filters.tipoDocumento;
  }

  if (filters.situacao) {
    payload.situacao = filters.situacao;
  }

  if (filters.vencimentoInicio) {
    payload.vencimento_de = formatDisplayDate(filters.vencimentoInicio);
  }

  if (filters.vencimentoFim) {
    payload.vencimento_ate = formatDisplayDate(filters.vencimentoFim);
  }

  if (filters.empresa) payload.empresa = filters.empresa;

  if (filters.cpfCnpjCedente.trim()) {
    payload.cedente = normalizeDocument(filters.cpfCnpjCedente);
  }

  if (filters.nomeSacado.trim()) {
    payload.nome_sacado = filters.nomeSacado.trim();
  }

  if (filters.cpfCnpjSacado.trim()) {
    payload.cpf_cnpj_sacado = normalizeDocument(filters.cpfCnpjSacado);
  }

  if (filters.bordero.trim()) {
    payload.bordero = filters.bordero.trim();
  }

  if (filters.numeroDocumento.trim()) {
    payload.n_documento = filters.numeroDocumento.trim();
  }

  if (filters.numeroNotaFiscal.trim()) {
    payload.n_nota_fiscal = Number(filters.numeroNotaFiscal);
  }

  return payload;
};

const mapApiRow = (item, filters) => ({
  id: normalizeTitleId(item.CODTITULO),
  borderoNumero: item.BORDERO ? String(item.BORDERO) : '-',
  carteira: item.CARTEIRA || item.CEDENTE || '-',
  cedente: item.CEDENTE || '-',
  cliente: item.CEDENTE || item.CLIENTE || item.NOMECLIENTE || '-',
  cpfCnpjCedente: formatDocumentMask(item.CPFCNPJCEDENTE || ''),
  cpfCnpjSacado: formatDocumentMask(item.CPFCNPJSACADO || ''),
  documento: item.DOCUMENTO ? String(item.DOCUMENTO) : '-',
  notaFiscal: item.NOTAFISCAL ? String(item.NOTAFISCAL) : '-',
  sacado: item.SACADO || '-',
  statusAnalise: item.STATUSANALISE || '',
  situacao: resolveStatusLabel(item, filters.situacao),
  tipo: item.TIPODOCUMENTO || filters.tipoDocumento || 'BL',
  valor: Number(item.VALOR || 0),
  vencimento: formatDisplayDate(item.VENCIMENTO)
});

const buildErrorState = (error, fallbackMessage) => {
  if (error?.response?.status === 422 && error?.response?.data?.errors) {
    return error.response;
  }

  return (
    error?.response?.data?.error || error?.response?.data?.message || error?.message || fallbackMessage
  );
};

const formatPartyDisplay = (document, name) => {
  const normalizedName = name || '-';
  const normalizedDocument = document && document !== '-' ? document : '';

  return normalizedDocument ? `${normalizedDocument} - ${normalizedName}` : normalizedName;
};

const PartyCell = ({ document, name, stacked = false }) => {
  const normalizedName = name || '-';
  const normalizedDocument = document && document !== '-' ? document : null;

  if (!stacked) {
    return (
      <Box className="financeiro-deferimento-lote__party">{formatPartyDisplay(document, name)}</Box>
    );
  }

  return (
    <Box className="financeiro-deferimento-lote__party financeiro-deferimento-lote__party--stacked">
      {normalizedDocument && (
        <Typography className="financeiro-deferimento-lote__party-document">
          {normalizedDocument}
        </Typography>
      )}

      <Typography className="financeiro-deferimento-lote__party-name">{normalizedName}</Typography>
    </Box>
  );
};

const fetchSelectionRowsRecursively = async ({ codUsuario, currentPage = 1, filters, rows = [] }) => {
  const payload = buildListPayload({
    codUsuario,
    filters,
    page: currentPage,
    perPage: SELECTION_PAGE_SIZE
  });

  const response = await listarTitulosEmAbertoHojeBL(payload);
  const responseData = response?.data || {};
  const responseRows = Array.isArray(responseData.data) ? responseData.data : [];
  const mappedRows = responseRows.map(item => mapApiRow(item, filters));
  const nextRows = [...rows, ...mappedRows];
  const lastPage = Number(responseData?.meta?.last_page || 1);

  if (currentPage >= lastPage) {
    return nextRows;
  }

  return fetchSelectionRowsRecursively({
    codUsuario,
    currentPage: currentPage + 1,
    filters,
    rows: nextRows
  });
};

const BatchSummaryCard = ({ summary }) => {
  if (!summary) return null;

  const blockedPreview = (summary.bloqueados || []).slice(0, 3);
  const missingPreview = (summary.nao_encontrados || []).slice(0, 3);

  return (
    <Box className="financeiro-deferimento-lote__summary-card">
      <Typography className="financeiro-deferimento-lote__summary-title">
        {summary.mensagem || 'Resumo do processamento'}
      </Typography>

      <Box className="financeiro-deferimento-lote__summary-stats">
        {[
          { label: 'Recebidos', value: summary.total_recebido ?? 0 },
          { label: 'Processados', value: summary.total_processado ?? 0 },
          { label: 'Bloqueados', value: summary.total_bloqueado ?? 0 },
          { label: 'Ignorados', value: summary.total_ignorado ?? 0 },
          { label: 'Não encontrados', value: summary.total_nao_encontrado ?? 0 }
        ].map(item => (
          <Box className="financeiro-deferimento-lote__summary-stat" key={item.label}>
            <Typography className="financeiro-deferimento-lote__summary-stat-label">
              {item.label}
            </Typography>
            <Typography className="financeiro-deferimento-lote__summary-stat-value">
              {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {summary.feedback_email_enviado && (
        <Typography className="financeiro-deferimento-lote__summary-feedback">
          O feedback do processamento foi enviado por e-mail.
        </Typography>
      )}

      {blockedPreview.length > 0 && (
        <Box className="financeiro-deferimento-lote__summary-group">
          <Typography className="financeiro-deferimento-lote__summary-group-title financeiro-deferimento-lote__summary-group-title--blocked">
            Bloqueados
          </Typography>
          {blockedPreview.map(item => (
            <Typography
              className="financeiro-deferimento-lote__summary-group-item"
              key={`bloqueado-${item.cod_operacao}`}
            >
              Operação {item.cod_operacao}: {item.motivo}
            </Typography>
          ))}
        </Box>
      )}

      {missingPreview.length > 0 && (
        <Box className="financeiro-deferimento-lote__summary-group">
          <Typography className="financeiro-deferimento-lote__summary-group-title financeiro-deferimento-lote__summary-group-title--missing">
            Não encontrados
          </Typography>
          {missingPreview.map(item => (
            <Typography
              className="financeiro-deferimento-lote__summary-group-item"
              key={`nao-encontrado-${item.cod_titulo}`}
            >
              Título {item.cod_titulo}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

const FilterSelectField = ({
  ariaLabel,
  disabled = false,
  emptyOptionLabel = 'Todos',
  includeEmptyOption = true,
  label,
  onChange,
  options,
  placeholder,
  value,
  width
}) => {
  const selectedOption = options.find(option => option.value === value);

  return (
    <Box
      className="financeiro-deferimento-lote__filter-field"
      style={{ '--filter-width': `${width}px` }}
    >
      <Typography className="financeiro-deferimento-lote__filter-label">{label}</Typography>
      <Select
        aria-label={ariaLabel}
        className="financeiro-deferimento-lote__select-field"
        disabled={disabled}
        displayEmpty
        fullWidth
        onChange={event => onChange(event.target.value)}
        renderValue={selectedValue =>
          selectedValue ? (
            (selectedOption?.label ?? selectedValue)
          ) : (
            <span className="financeiro-deferimento-lote__placeholder">{placeholder}</span>
          )
        }
        value={value}
      >
        {includeEmptyOption && (
          <MenuItem value="">
            <em>{emptyOptionLabel}</em>
          </MenuItem>
        )}
        {options.map(option => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

const FilterTextField = ({ ariaLabel, inputMode, label, onChange, placeholder, value, width }) => (
  <Box className="financeiro-deferimento-lote__filter-field" style={{ '--filter-width': `${width}px` }}>
    <Typography className="financeiro-deferimento-lote__filter-label">{label}</Typography>
    <TextField
      aria-label={ariaLabel}
      className="financeiro-deferimento-lote__text-field"
      fullWidth
      inputMode={inputMode}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
      value={value}
    />
  </Box>
);

const StatusBadge = ({ status, labelClassName = '' }) => {
  const styles = getStatusStyles(status);

  return (
    <Box
      className="financeiro-deferimento-lote__status-badge"
      style={{
        '--status-bg': styles.backgroundColor,
        '--status-color': styles.color,
        '--status-dot': styles.dotColor,
        '--status-height': `${styles.height}px`,
        '--status-min-width': `${styles.minWidth}px`
      }}
    >
      <Box className="financeiro-deferimento-lote__status-dot" />
      <Typography className={`financeiro-deferimento-lote__status-label ${labelClassName}`.trim()}>
        {resolveStatusDisplayLabel(status)}
      </Typography>
    </Box>
  );
};

const TipoBadge = ({ tipo }) => <Box className="financeiro-deferimento-lote__type-badge">{tipo}</Box>;

const SortableHeaderLabel = ({ active = false, direction = 'asc', label, onClick }) => {
  const SortIcon = !active
    ? SwapVertRoundedIcon
    : direction === 'asc'
      ? ArrowUpwardRoundedIcon
      : ArrowDownwardRoundedIcon;

  return (
    <Box
      className={`financeiro-deferimento-lote__sort-button${
        active ? ' financeiro-deferimento-lote__sort-button--active' : ''
      }`}
      component="button"
      onClick={onClick}
      type="button"
    >
      <span>{label}</span>
      <SortIcon className="financeiro-deferimento-lote__sort-icon" />
    </Box>
  );
};

const buildVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 1) return [1];

  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const normalizedCurrentPage = Math.min(Math.max(currentPage, 1), totalPages);

  if (normalizedCurrentPage <= 2) {
    return [1, 2, 3, '...', totalPages];
  }

  if (normalizedCurrentPage >= totalPages - 1) {
    return [1, '...', totalPages - 2, totalPages - 1, totalPages];
  }

  return [
    1,
    '...',
    normalizedCurrentPage - 1,
    normalizedCurrentPage,
    normalizedCurrentPage + 1,
    '...',
    totalPages
  ];
};

const TableLoadingSkeleton = ({ rowCount = TABLE_SKELETON_ROW_COUNT }) =>
  Array.from({ length: rowCount }, (_, index) => (
    <TableRow className="financeiro-deferimento-lote__table-row" key={`table-skeleton-${index}`}>
      <TableCell
        className="financeiro-deferimento-lote__table-body-cell financeiro-deferimento-lote__table-cell--checkbox"
        padding="checkbox"
        style={{ width: `${COLUMN_WIDTHS.select}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-checkbox" variant="rounded" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.situacao}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-badge" variant="rounded" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.bordero}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text financeiro-deferimento-lote__table-skeleton-text--short" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.carteira}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.notaFiscal}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text financeiro-deferimento-lote__table-skeleton-text--short" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.documento}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text" />
      </TableCell>

      <TableCell
        align="center"
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.tipo}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-type" variant="rounded" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.vencimento}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text" />
      </TableCell>

      <TableCell
        align="right"
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.valor}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text financeiro-deferimento-lote__table-skeleton-text--medium" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.cliente}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text" />
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text financeiro-deferimento-lote__table-skeleton-text--medium" />
      </TableCell>

      <TableCell
        className="financeiro-deferimento-lote__table-body-cell"
        style={{ width: `${COLUMN_WIDTHS.sacado}px` }}
      >
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text" />
        <Skeleton className="financeiro-deferimento-lote__table-skeleton-text financeiro-deferimento-lote__table-skeleton-text--medium" />
      </TableCell>
    </TableRow>
  ));

const PaginationControls = ({ currentPage, disabled = false, onPageChange, totalPages }) => {
  const safeTotalPages = Math.max(1, totalPages);
  const visiblePages = buildVisiblePages(currentPage, safeTotalPages);
  const isFirstPage = currentPage <= 1;
  const isLastPage = currentPage >= safeTotalPages;

  const changePage = nextPage => {
    if (nextPage >= 1 && nextPage <= safeTotalPages) {
      onPageChange(nextPage);
    }
  };

  return (
    <Box className="financeiro-deferimento-lote__pagination-controls">
      <Box
        aria-label="Primeira página"
        className="financeiro-deferimento-lote__pagination-arrow"
        component="button"
        disabled={disabled || isFirstPage}
        onClick={() => changePage(1)}
        type="button"
      >
        <span className="financeiro-deferimento-lote__pagination-double-chevron">
          <KeyboardArrowLeftRoundedIcon className="financeiro-deferimento-lote__pagination-chevron-icon" />
          <KeyboardArrowLeftRoundedIcon className="financeiro-deferimento-lote__pagination-chevron-icon" />
        </span>
      </Box>

      <Box
        className="financeiro-deferimento-lote__pagination-nav"
        component="button"
        disabled={disabled || isFirstPage}
        onClick={() => changePage(currentPage - 1)}
        type="button"
      >
        Anterior
      </Box>

      <Box className="financeiro-deferimento-lote__pagination-pages">
        {visiblePages.map((pageNumber, index) => {
          if (pageNumber === '...') {
            return (
              <Typography
                className="financeiro-deferimento-lote__pagination-ellipsis"
                key={`ellipsis-${index}`}
              >
                ...
              </Typography>
            );
          }

          return (
            <Box
              aria-current={pageNumber === currentPage ? 'page' : undefined}
              className={`financeiro-deferimento-lote__pagination-page${
                pageNumber === currentPage ? ' financeiro-deferimento-lote__pagination-page--active' : ''
              }`}
              component="button"
              disabled={disabled}
              key={pageNumber}
              onClick={() => changePage(pageNumber)}
              type="button"
            >
              {pageNumber}
            </Box>
          );
        })}
      </Box>

      <Box
        className="financeiro-deferimento-lote__pagination-nav"
        component="button"
        disabled={disabled || isLastPage}
        onClick={() => changePage(currentPage + 1)}
        type="button"
      >
        Próximo
      </Box>

      <Box
        aria-label="Última página"
        className="financeiro-deferimento-lote__pagination-arrow"
        component="button"
        disabled={disabled || isLastPage}
        onClick={() => changePage(safeTotalPages)}
        type="button"
      >
        <span className="financeiro-deferimento-lote__pagination-double-chevron">
          <KeyboardArrowRightRoundedIcon className="financeiro-deferimento-lote__pagination-chevron-icon" />
          <KeyboardArrowRightRoundedIcon className="financeiro-deferimento-lote__pagination-chevron-icon" />
        </span>
      </Box>
    </Box>
  );
};

export const DeferimentoLoteContent = () => {
  const [draftFilters, setDraftFilters] = useState(createInitialFilters);
  const [appliedFilters, setAppliedFilters] = useState(createInitialFilters);
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    lastPage: 1,
    perPage: DEFAULT_PAGE_SIZE
  });
  const [selectedIds, setSelectedIds] = useState([]);
  const [totalSelectableCount, setTotalSelectableCount] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [batchSummary, setBatchSummary] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [isSubmittingBatch, setIsSubmittingBatch] = useState(false);
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    direction: 'asc',
    field: ''
  });

  const totalPages = Math.max(1, pagination.lastPage || 1);
  const activeFiltersCount = countActiveFilters(draftFilters);
  const selectionCount = selectedIds.length;
  const sortedRows = sortRows(rows, sortConfig);
  const selectableRowsOnPage = sortedRows.filter(isRowSelectable);
  const currentPageCoversAllResults = pagination.total <= rows.length;
  const firstItemOnPage = pagination.total === 0 ? 0 : (page - 1) * pagination.perPage + 1;
  const lastItemOnPage =
    pagination.total === 0 ? 0 : Math.min(page * pagination.perPage, pagination.total);
  const allFilteredSelected =
    totalSelectableCount !== null
      ? totalSelectableCount > 0 && selectionCount === totalSelectableCount
      : currentPageCoversAllResults &&
        selectableRowsOnPage.length > 0 &&
        selectableRowsOnPage.every(row => selectedIds.includes(row.id));
  const someFilteredSelected = selectionCount > 0 && !allFilteredSelected;
  const paginationDisabled = pagination.total <= 0;
  const isIndeferidoFilterApplied = appliedFilters.situacao === 'indeferido';
  const selectAllDisabled =
    isLoadingList ||
    isSelectingAll ||
    isSubmittingBatch ||
    isIndeferidoFilterApplied ||
    pagination.total === 0 ||
    (currentPageCoversAllResults && selectableRowsOnPage.length === 0);
  const paginationSummaryText = `Mostrando ${formatNumber(firstItemOnPage)} a ${formatNumber(
    lastItemOnPage
  )}, entre ${formatNumber(pagination.total)} resultados.`;

  const loadRows = useCallback(
    async ({ targetPage = page, filters = appliedFilters } = {}) => {
      const codUsuario = getCodUsuarioLogado();

      if (!codUsuario) {
        setRows([]);
        setTotalSelectableCount(null);
        setPagination({
          total: 0,
          lastPage: 1,
          perPage: perPage
        });
        setRequestError('Não foi possível identificar o usuário autenticado.');
        return;
      }

      setIsLoadingList(true);
      setRequestError(false);

      try {
        const payload = buildListPayload({
          codUsuario,
          filters,
          page: targetPage,
          perPage
        });

        const response = await listarTitulosEmAbertoHojeBL(payload);
        const responseData = response?.data || {};
        const responseRows = Array.isArray(responseData.data) ? responseData.data : [];
        const meta = responseData.meta || {};
        const nextPage = Number(meta.page || targetPage || 1);
        const nextPerPage = Number(meta.per_page || perPage);
        const nextLastPage = Number(meta.last_page || 1);
        const total = Number(responseData.total || 0);
        const mappedRows = responseRows.map(item => mapApiRow(item, filters));

        setRows(mappedRows);
        setSelectedIds(previousSelectedIds =>
          previousSelectedIds.filter(
            id => !mappedRows.some(row => row.id === id && !isRowSelectable(row))
          )
        );
        setTotalSelectableCount(previousCount =>
          nextLastPage <= 1 ? mappedRows.filter(isRowSelectable).length : previousCount
        );
        setPagination({
          total,
          lastPage: nextLastPage,
          perPage: nextPerPage
        });

        if (nextPerPage !== perPage) {
          setPerPage(nextPerPage);
        }

        if (nextPage !== page) {
          setPage(nextPage);
        }
      } catch (error) {
        setRows([]);
        setTotalSelectableCount(null);
        setPagination({
          total: 0,
          lastPage: 1,
          perPage
        });
        setRequestError(buildErrorState(error, 'Ocorreu um erro ao listar os títulos em aberto.'));
      } finally {
        setIsLoadingList(false);
      }
    },
    [appliedFilters, page, perPage]
  );

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  useEffect(() => {
    let isMounted = true;

    const loadCompanyOptions = async () => {
      setIsLoadingCompanies(true);

      try {
        const response = await listarEmpresasIndeferimentoLote({ limit: 500 });
        const responseData = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextOptions = responseData.map(option => ({
          value: String(option.cod_empresa ?? option.value ?? ''),
          label: option.label || `${option.cod_empresa} - ${option.nome}`
        }));

        if (isMounted) {
          setCompanyOptions(nextOptions.filter(option => option.value));
        }
      } catch (error) {
        if (isMounted) {
          setCompanyOptions([]);
        }

        openSnackbarMain(
          buildErrorState(error, 'Ocorreu um erro ao carregar as empresas do filtro.'),
          'error'
        );
      } finally {
        if (isMounted) {
          setIsLoadingCompanies(false);
        }
      }
    };

    loadCompanyOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  const updateDraftFilter = (fieldName, nextValue) => {
    setDraftFilters(previousFilters => ({
      ...previousFilters,
      [fieldName]: nextValue
    }));
  };

  const clearSelection = () => {
    setSelectedIds([]);
    setTotalSelectableCount(null);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setPage(1);
    clearSelection();
    setBatchSummary(null);
    setRequestError(false);
  };

  const handleClearFilters = () => {
    const clearedFilters = createInitialFilters();

    setDraftFilters(clearedFilters);
    setAppliedFilters(clearedFilters);
    setPage(1);
    clearSelection();
    setBatchSummary(null);
    setRequestError(false);
  };

  const handlePerPageChange = nextPerPage => {
    setPerPage(Number(nextPerPage));
    setPage(1);
    clearSelection();
  };

  const handleSortChange = field => {
    setSortConfig(previousSortConfig => {
      if (previousSortConfig.field === field) {
        return {
          ...previousSortConfig,
          direction: previousSortConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      }

      return {
        direction: 'asc',
        field
      };
    });
  };

  const handleSelectAllFiltered = async checked => {
    if (!checked) {
      clearSelection();
      return;
    }

    if (appliedFilters.situacao === 'indeferido') {
      return;
    }

    if (pagination.total === 0) return;

    setRequestError(false);
    setBatchSummary(null);

    const codUsuario = getCodUsuarioLogado();

    if (currentPageCoversAllResults) {
      const selectableIds = selectableRowsOnPage.map(row => row.id);
      setSelectedIds(selectableIds);
      setTotalSelectableCount(selectableIds.length);
      return;
    }

    if (!codUsuario) {
      setRequestError('Não foi possível identificar o usuário autenticado.');
      return;
    }

    setIsSelectingAll(true);

    try {
      const allRows = await fetchSelectionRowsRecursively({
        codUsuario,
        filters: appliedFilters
      });
      const selectableIds = allRows.filter(isRowSelectable).map(row => row.id);
      setSelectedIds(selectableIds);
      setTotalSelectableCount(selectableIds.length);
    } catch (error) {
      setRequestError(
        buildErrorState(error, 'Ocorreu um erro ao selecionar todos os títulos filtrados.')
      );
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleSelectRow = (row, checked) => {
    if (checked && !isRowSelectable(row)) return;

    setSelectedIds(previousSelectedIds => {
      if (checked) {
        return Array.from(new Set([...previousSelectedIds, row.id]));
      }

      return previousSelectedIds.filter(id => id !== row.id);
    });
  };

  const handleConfirmIndeferir = async () => {
    if (selectionCount === 0) return;

    setIsSubmittingBatch(true);
    setConfirmDialogOpen(false);
    setRequestError(false);
    setBatchSummary(null);

    try {
      const codTitulos = selectedIds.map(id => Number(id)).filter(id => Number.isFinite(id));

      const response = await indeferirTitulosLote({
        cod_usuario: getCodUsuarioLogado(),
        cod_titulos: codTitulos
      });

      if (response.status === 202 || response?.data?.modo === 'assincrono') {
        openSnackbarMain(
          response?.data?.mensagem || 'Indeferimento em lote enviado para processamento assíncrono.',
          'info'
        );
      } else {
        const responseSummary = response?.data || {};
        setBatchSummary(responseSummary);
        openSnackbarMain(
          responseSummary.mensagem || 'Processamento de indeferimento em lote concluído.',
          'success'
        );
      }

      clearSelection();
      await loadRows({ targetPage: page });
    } catch (error) {
      setRequestError(buildErrorState(error, 'Ocorreu um erro ao processar o indeferimento em lote.'));
    } finally {
      setIsSubmittingBatch(false);
    }
  };

  return (
    <Box className="financeiro-deferimento-lote">
      <Box
        className="financeiro-deferimento-lote__card financeiro-deferimento-lote__hero-card"
        data-node-id="17:6879"
      >
        <Typography className="financeiro-deferimento-lote__page-title">Controle de Baixas</Typography>

        <Typography className="financeiro-deferimento-lote__page-subtitle">
          Gerencie e visualize todos os registros de cobrança
        </Typography>
      </Box>

      <Box className="financeiro-deferimento-lote__card" data-node-id="17:6884">
        <Box className="financeiro-deferimento-lote__section-header" data-node-id="17:6885">
          <Box className="financeiro-deferimento-lote__section-header-group">
            <FilterAltOutlinedIcon className="financeiro-deferimento-lote__section-icon" />
            <Typography className="financeiro-deferimento-lote__section-title">
              Filtros de Pesquisa
            </Typography>

            <Box className="financeiro-deferimento-lote__active-filters">
              {activeFiltersCount} {activeFiltersCount === 1 ? 'ativo' : 'ativos'}
            </Box>
          </Box>

          <Button
            className="financeiro-deferimento-lote__toggle-filters"
            endIcon={filtersExpanded ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            onClick={() => setFiltersExpanded(previousState => !previousState)}
          >
            {filtersExpanded ? 'Menos filtros' : 'Mais filtros'}
          </Button>
        </Box>

        <Box className="financeiro-deferimento-lote__section-body" data-node-id="17:6897">
          <Box className="financeiro-deferimento-lote__filters-row financeiro-deferimento-lote__filters-row--primary">
            <FilterSelectField
              ariaLabel="Situação"
              label="Situação"
              onChange={value => updateDraftFilter('situacao', value)}
              options={STATUS_OPTIONS}
              placeholder="Todos"
              value={draftFilters.situacao}
              width={182.219}
            />

            <FilterSelectField
              ariaLabel="Tipo de Documento"
              disabled
              emptyOptionLabel="Selecione"
              includeEmptyOption={false}
              label="Tipo de Documento"
              onChange={value => updateDraftFilter('tipoDocumento', value)}
              options={TYPE_OPTIONS}
              placeholder="Selecione"
              value={draftFilters.tipoDocumento}
              width={182.219}
            />

            <FilterSelectField
              ariaLabel="Empresa"
              disabled={isLoadingCompanies}
              label="Empresa"
              onChange={value => updateDraftFilter('empresa', value)}
              options={companyOptions}
              placeholder={isLoadingCompanies ? 'Carregando...' : 'Todos'}
              value={draftFilters.empresa}
              width={182.229}
            />

            <FilterTextField
              ariaLabel="CPF/CNPJ Cedente"
              inputMode="text"
              label="CPF/CNPJ Cedente"
              onChange={value => updateDraftFilter('cpfCnpjCedente', formatDocumentMask(value))}
              placeholder="00.000.000/0000-00"
              value={draftFilters.cpfCnpjCedente}
              width={182.219}
            />

            <Box
              className="financeiro-deferimento-lote__filter-field financeiro-deferimento-lote__filter-field--date-range"
              style={{ '--filter-width': '380.448px' }}
            >
              <Typography className="financeiro-deferimento-lote__filter-label">Vencimento</Typography>
              <LocalizationProvider dateAdapter={AdapterMoment} localeText={localizacaoBrasil}>
                <Box className="financeiro-deferimento-lote__date-range">
                  <DatePicker
                    format="DD/MM/YYYY"
                    onChange={value =>
                      updateDraftFilter(
                        'vencimentoInicio',
                        value && value.isValid() ? value.format('DD/MM/YYYY') : ''
                      )
                    }
                    slotProps={{
                      textField: {
                        'aria-label': 'Vencimento inicial',
                        className: 'financeiro-deferimento-lote__date-range-input',
                        fullWidth: true
                      }
                    }}
                    value={parseMomentDate(draftFilters.vencimentoInicio)}
                  />

                  <Typography className="financeiro-deferimento-lote__date-separator">até</Typography>

                  <DatePicker
                    format="DD/MM/YYYY"
                    onChange={value =>
                      updateDraftFilter(
                        'vencimentoFim',
                        value && value.isValid() ? value.format('DD/MM/YYYY') : ''
                      )
                    }
                    slotProps={{
                      textField: {
                        'aria-label': 'Vencimento final',
                        className: 'financeiro-deferimento-lote__date-range-input',
                        fullWidth: true
                      }
                    }}
                    value={parseMomentDate(draftFilters.vencimentoFim)}
                  />
                </Box>
              </LocalizationProvider>
            </Box>
          </Box>

          {filtersExpanded && (
            <Box className="financeiro-deferimento-lote__secondary-filters">
              <Box className="financeiro-deferimento-lote__filters-row">
                <FilterTextField
                  ariaLabel="Nome Sacado"
                  label="Nome Sacado"
                  onChange={value => updateDraftFilter('nomeSacado', value)}
                  placeholder="Digite o nome"
                  value={draftFilters.nomeSacado}
                  width={182.219}
                />

                <FilterTextField
                  ariaLabel="CPF/CNPJ Sacado"
                  inputMode="text"
                  label="CPF/CNPJ Sacado"
                  onChange={value => updateDraftFilter('cpfCnpjSacado', formatDocumentMask(value))}
                  placeholder="000.000.000-00"
                  value={draftFilters.cpfCnpjSacado}
                  width={182.219}
                />

                <FilterTextField
                  ariaLabel="Borderô"
                  inputMode="text"
                  label="Borderô"
                  onChange={value => updateDraftFilter('bordero', sanitizeCommaSeparatedNumeric(value))}
                  placeholder="Nº do borderô"
                  value={draftFilters.bordero}
                  width={182.229}
                />

                <FilterTextField
                  ariaLabel="Nº Documento"
                  inputMode="text"
                  label="Nº Documento"
                  onChange={value =>
                    updateDraftFilter('numeroDocumento', sanitizeCommaSeparatedNumeric(value))
                  }
                  placeholder="Nº do documento"
                  value={draftFilters.numeroDocumento}
                  width={182.219}
                />

                <FilterTextField
                  ariaLabel="Nº Nota Fiscal"
                  inputMode="numeric"
                  label="Nº Nota Fiscal"
                  onChange={value => updateDraftFilter('numeroNotaFiscal', value.replace(/\D/g, ''))}
                  placeholder="Nº da NF"
                  value={draftFilters.numeroNotaFiscal}
                  width={182.219}
                />
              </Box>
            </Box>
          )}

          <Box className="financeiro-deferimento-lote__filter-actions" data-node-id="17:6967">
            <Button
              className="financeiro-deferimento-lote__button-primary"
              onClick={handleApplyFilters}
              startIcon={<SearchOutlinedIcon />}
            >
              Filtrar
            </Button>

            <Button
              className="financeiro-deferimento-lote__button-secondary"
              onClick={handleClearFilters}
              startIcon={<ClearOutlinedIcon />}
            >
              Limpar Filtros
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        className="financeiro-deferimento-lote__card financeiro-deferimento-lote__results-card"
        data-node-id="17:6977"
      >
        <Box className="financeiro-deferimento-lote__results-header" data-node-id="17:6978">
          <Typography className="financeiro-deferimento-lote__results-title" component="div">
            <Box component="span">Resultados da Pesquisa </Box>
            <Box className="financeiro-deferimento-lote__results-meta" component="span">
              Mostrando {firstItemOnPage}
              {'\u2013'}
              {lastItemOnPage} de {pagination.total} registros
            </Box>
          </Typography>
        </Box>

        {requestError && (
          <Box className="financeiro-deferimento-lote__error-wrapper">
            <AlertErroMain exibeErro={requestError} escondeErro={setRequestError} />
          </Box>
        )}

        <BatchSummaryCard summary={batchSummary} />

        {selectionCount > 0 && (
          <Box className="financeiro-deferimento-lote__selection-bar">
            <Box className="financeiro-deferimento-lote__selection-info">
              <Box className="financeiro-deferimento-lote__selection-dot" />

              <Typography className="financeiro-deferimento-lote__selection-text">
                {selectionCount} {selectionCount === 1 ? 'item selecionado' : 'itens selecionados'}
              </Typography>

              <Button className="financeiro-deferimento-lote__selection-clear" onClick={clearSelection}>
                Limpar seleção
              </Button>
            </Box>

            <Box className="financeiro-deferimento-lote__selection-actions">
              <Button
                className="financeiro-deferimento-lote__button-danger"
                onClick={() => setConfirmDialogOpen(true)}
              >
                Indeferir
              </Button>
            </Box>
          </Box>
        )}

        <TableContainer className="financeiro-deferimento-lote__table-container">
          <Table className="financeiro-deferimento-lote__table">
            <TableHead>
              <TableRow className="financeiro-deferimento-lote__table-row financeiro-deferimento-lote__table-row--head">
                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell financeiro-deferimento-lote__table-cell--checkbox"
                  padding="checkbox"
                  style={{ width: `${COLUMN_WIDTHS.select}px` }}
                >
                  <Checkbox
                    className="financeiro-deferimento-lote__checkbox"
                    checked={allFilteredSelected}
                    disabled={selectAllDisabled}
                    inputProps={{
                      'aria-label': 'Selecionar todos os registros'
                    }}
                    indeterminate={someFilteredSelected}
                    onChange={event => handleSelectAllFiltered(event.target.checked)}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.situacao}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'situacao'}
                    direction={sortConfig.direction}
                    label="Situação"
                    onClick={() => handleSortChange('situacao')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.bordero}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'borderoNumero'}
                    direction={sortConfig.direction}
                    label="Borderô"
                    onClick={() => handleSortChange('borderoNumero')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.carteira}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'carteira'}
                    direction={sortConfig.direction}
                    label="Carteira"
                    onClick={() => handleSortChange('carteira')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.notaFiscal}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'notaFiscal'}
                    direction={sortConfig.direction}
                    label="NF"
                    onClick={() => handleSortChange('notaFiscal')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.documento}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'documento'}
                    direction={sortConfig.direction}
                    label="Documento"
                    onClick={() => handleSortChange('documento')}
                  />
                </TableCell>

                <TableCell
                  align="center"
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.tipo}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'tipo'}
                    direction={sortConfig.direction}
                    label="Tipo"
                    onClick={() => handleSortChange('tipo')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.vencimento}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'vencimento'}
                    direction={sortConfig.direction}
                    label="Vencimento"
                    onClick={() => handleSortChange('vencimento')}
                  />
                </TableCell>

                <TableCell
                  align="right"
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.valor}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'valor'}
                    direction={sortConfig.direction}
                    label="Valor"
                    onClick={() => handleSortChange('valor')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.cliente}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'cliente'}
                    direction={sortConfig.direction}
                    label="Cedente"
                    onClick={() => handleSortChange('cliente')}
                  />
                </TableCell>

                <TableCell
                  className="financeiro-deferimento-lote__table-header-cell"
                  style={{ width: `${COLUMN_WIDTHS.sacado}px` }}
                >
                  <SortableHeaderLabel
                    active={sortConfig.field === 'sacado'}
                    direction={sortConfig.direction}
                    label="Sacado"
                    onClick={() => handleSortChange('sacado')}
                  />
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {isLoadingList ? (
                <TableLoadingSkeleton />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell className="financeiro-deferimento-lote__table-empty" colSpan={11}>
                    Nenhum registro encontrado para os filtros aplicados.
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows.map(row => (
                  <TableRow className="financeiro-deferimento-lote__table-row" key={row.id}>
                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell financeiro-deferimento-lote__table-cell--checkbox"
                      padding="checkbox"
                      style={{ width: `${COLUMN_WIDTHS.select}px` }}
                    >
                      <Checkbox
                        className="financeiro-deferimento-lote__checkbox"
                        checked={selectedIds.includes(row.id)}
                        disabled={isSubmittingBatch || !isRowSelectable(row)}
                        inputProps={{
                          'aria-label': `Selecionar registro ${row.id}`
                        }}
                        onChange={event => handleSelectRow(row, event.target.checked)}
                      />
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.situacao}px` }}
                    >
                      <StatusBadge
                        labelClassName="financeiro-deferimento-lote__status-label--situacao"
                        status={row.situacao}
                      />
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.bordero}px` }}
                    >
                      <Typography className="financeiro-deferimento-lote__table-strong-text">
                        {row.borderoNumero}
                      </Typography>
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.carteira}px` }}
                    >
                      {row.carteira}
                    </TableCell>

                    <TableCell
                      className={`financeiro-deferimento-lote__table-body-cell${
                        row.notaFiscal === '-'
                          ? ' financeiro-deferimento-lote__table-body-cell--muted'
                          : ''
                      }`}
                      style={{ width: `${COLUMN_WIDTHS.notaFiscal}px` }}
                    >
                      {row.notaFiscal}
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.documento}px` }}
                    >
                      {row.documento}
                    </TableCell>

                    <TableCell
                      align="center"
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.tipo}px` }}
                    >
                      <TipoBadge tipo={row.tipo} />
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell"
                      style={{ width: `${COLUMN_WIDTHS.vencimento}px` }}
                    >
                      {row.vencimento}
                    </TableCell>

                    <TableCell
                      align="right"
                      className="financeiro-deferimento-lote__table-body-cell financeiro-deferimento-lote__table-body-cell--strong"
                      style={{ width: `${COLUMN_WIDTHS.valor}px` }}
                    >
                      {formatCurrency(row.valor)}
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell financeiro-deferimento-lote__table-body-cell--strong"
                      style={{ width: `${COLUMN_WIDTHS.cliente}px` }}
                    >
                      <PartyCell document={row.cpfCnpjCedente} name={row.cliente} stacked={true} />
                    </TableCell>

                    <TableCell
                      className="financeiro-deferimento-lote__table-body-cell financeiro-deferimento-lote__table-body-cell--strong"
                      style={{ width: `${COLUMN_WIDTHS.sacado}px` }}
                    >
                      <PartyCell document={row.cpfCnpjSacado} name={row.sacado} stacked={true} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className="financeiro-deferimento-lote__pagination" data-node-id="17:7953">
          <Typography className="financeiro-deferimento-lote__pagination-results">
            {paginationSummaryText}
          </Typography>

          <Box className="financeiro-deferimento-lote__pagination-right">
            <PaginationControls
              currentPage={page}
              disabled={paginationDisabled}
              onPageChange={setPage}
              totalPages={totalPages}
            />

            <Box className="financeiro-deferimento-lote__pagination-page-size">
              <Typography className="financeiro-deferimento-lote__pagination-label">
                Registros por página
              </Typography>

              <Select
                aria-label="Registros por página"
                className="financeiro-deferimento-lote__pagination-select"
                disabled={paginationDisabled}
                onChange={event => handlePerPageChange(event.target.value)}
                value={String(perPage)}
              >
                {PAGE_SIZE_OPTIONS.map(option => (
                  <MenuItem key={option} value={String(option)}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>

        {(isSubmittingBatch || isSelectingAll) && <LoaderMain tipoImagem="circulo" tipoLoader="float" />}
      </Box>

      <Dialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        PaperProps={{
          className: 'financeiro-deferimento-lote__dialog-paper'
        }}
      >
        <DialogTitle className="financeiro-deferimento-lote__dialog-title">
          Confirmar indeferimento
        </DialogTitle>

        <DialogContent className="financeiro-deferimento-lote__dialog-content">
          <DialogContentText className="financeiro-deferimento-lote__dialog-text">
            Você está prestes a indeferir {selectionCount}{' '}
            {selectionCount === 1 ? 'registro selecionado' : 'registros selecionados'}. Deseja continuar?
          </DialogContentText>
        </DialogContent>

        <DialogActions className="financeiro-deferimento-lote__dialog-actions">
          <Button
            className="financeiro-deferimento-lote__dialog-button financeiro-deferimento-lote__dialog-button--secondary"
            onClick={() => setConfirmDialogOpen(false)}
          >
            Cancelar
          </Button>

          <Button
            className="financeiro-deferimento-lote__dialog-button financeiro-deferimento-lote__dialog-button--danger"
            onClick={handleConfirmIndeferir}
          >
            Indeferir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const DeferimentoLote = ({ escopoAcesso }) => (
  <Main className="financeiro-deferimento-lote-page" escopoAcesso={escopoAcesso}>
    <DeferimentoLoteContent />
  </Main>
);

export default DeferimentoLote;
